import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const COUNTRIES = [
  { value: 'fr', label: 'France' },
  { value: 'uk', label: 'UK' },
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

function defaultMonth() {
  // Default to current month in YYYY-MM format
  return `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
}

function BookingRow({ booking, showExclusion }) {
  // Link to the booking-trace diagnostic (the closest existing per-property probe)
  const probeUrl = `/diagnostics?probe=${booking.property_id}`;
  return (
    <tr className="border-b last:border-0 text-xs">
      <td className="px-2 py-1.5 font-mono">
        <a
          href={probeUrl}
          className="text-blue-600 dark:text-blue-400 hover:underline"
          title={`Property ${booking.property_id} — open in probe`}
        >
          {booking.property_name || booking.property_id}
        </a>
      </td>
      <td className="px-2 py-1.5 font-mono text-muted-foreground">{booking.booking_id}</td>
      <td className="px-2 py-1.5">{booking.platform}</td>
      <td className="px-2 py-1.5">{booking.status}</td>
      <td className="px-2 py-1.5 font-mono">{booking.check_in}</td>
      <td className="px-2 py-1.5 font-mono">{booking.check_out}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{booking.nights_total}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{booking.nights_in_target_month}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{booking.nr_attributed_nights}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{booking.revenue ?? '-'}</td>
      <td className="px-2 py-1.5">{booking.guest_name}</td>
      {showExclusion && (
        <td className="px-2 py-1.5 text-muted-foreground max-w-[300px]">
          {booking.exclusion_reasons?.join('; ')}
        </td>
      )}
    </tr>
  );
}

function BookingTable({ bookings, title, badgeVariant, showExclusion }) {
  if (!bookings || bookings.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        {title}: none
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{title}</span>
        <Badge variant={badgeVariant} className="text-xs">{bookings.length}</Badge>
      </div>
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-2 py-1.5 font-medium text-xs">Property</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs">Booking ID</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs">Platform</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs">Status</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs">Check-in</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs">Check-out</th>
              <th className="text-right px-2 py-1.5 font-medium text-xs">Nights</th>
              <th className="text-right px-2 py-1.5 font-medium text-xs">In month</th>
              <th className="text-right px-2 py-1.5 font-medium text-xs">NR attrib</th>
              <th className="text-right px-2 py-1.5 font-medium text-xs">Revenue</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs">Guest</th>
              {showExclusion && (
                <th className="text-left px-2 py-1.5 font-medium text-xs">Exclusion reason</th>
              )}
            </tr>
          </thead>
          <tbody>
            {bookings.map((b, i) => (
              <BookingRow key={b.booking_key || i} booking={b} showExclusion={showExclusion} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PortfolioChartDivergencePanel() {
  const [country, setCountry] = useState('fr');
  const [month, setMonth] = useState(defaultMonth);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.portfolioChartDivergence({ country, month });
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const nr = results?.nightly_rate_path;
  const occ = results?.occupancy_path;
  const div = results?.divergence;
  const isConverged = results?.converged === true;
  const hasDivergence = div && (div.summary.only_in_nightly_rate_count > 0 || div.summary.only_in_occupancy_count > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Portfolio chart divergence audit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Country</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            />
          </div>
          <Button onClick={runAudit} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run audit
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-semibold">Nightly Rate path</p>
                <p className="text-xs text-muted-foreground">{nr?.description}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm mt-2">
                  <span className="text-muted-foreground">Bookings:</span>
                  <span className="text-right tabular-nums">{nr?.bookings_count}</span>
                  <span className="text-muted-foreground">Nights booked:</span>
                  <span className="text-right tabular-nums">{nr?.nights_booked_sum}</span>
                  <span className="text-muted-foreground">Revenue sum:</span>
                  <span className="text-right tabular-nums">{nr?.revenue_sum}</span>
                  <span className="text-muted-foreground">Rate computed:</span>
                  <span className="text-right tabular-nums font-semibold">{nr?.rate_computed}</span>
                </div>
              </div>
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-semibold">Occupancy path</p>
                <p className="text-xs text-muted-foreground">{occ?.description}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm mt-2">
                  <span className="text-muted-foreground">Bookings:</span>
                  <span className="text-right tabular-nums">{occ?.bookings_count}</span>
                  <span className="text-muted-foreground">Nights booked:</span>
                  <span className="text-right tabular-nums">{occ?.nights_booked_sum}</span>
                  <span className="text-muted-foreground">Nights available:</span>
                  <span className="text-right tabular-nums">{occ?.nights_available}</span>
                  <span className="text-muted-foreground">Occupancy:</span>
                  <span className="text-right tabular-nums font-semibold">{occ?.occupancy_computed}%</span>
                </div>
              </div>
            </div>

            {/* Divergence summary */}
            <div className="flex items-center gap-3 flex-wrap">
              {isConverged ? (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Converged</Badge>
              ) : hasDivergence ? (
                <Badge variant="destructive">Divergence found</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">No divergence</Badge>
              )}
              <span className="text-sm text-muted-foreground">
                {div?.summary.in_both_count} in both
                {div?.summary.only_in_occupancy_count > 0 && (
                  <>, {div.summary.only_in_occupancy_count} occupancy-only (IngestionError)</>
                )}
                {div?.summary.only_in_nightly_rate_count > 0 && (
                  <>, {div.summary.only_in_nightly_rate_count} nightly-rate-only (unexpected)</>
                )}
              </span>
            </div>

            {/* Divergence lists */}
            <BookingTable
              bookings={div?.only_in_occupancy}
              title="Only in Occupancy path (missing from Nightly Rate)"
              badgeVariant="destructive"
              showExclusion
            />
            <BookingTable
              bookings={div?.only_in_nightly_rate}
              title="Only in Nightly Rate path (missing from Occupancy)"
              badgeVariant="destructive"
              showExclusion
            />
            <BookingTable
              bookings={div?.in_both}
              title="In both paths"
              badgeVariant="outline"
              showExclusion={false}
            />

            {/* Known causes */}
            {results.known_divergence_causes && (
              <div className="text-xs text-muted-foreground border-t pt-2 space-y-1">
                <p className="font-medium">Known divergence causes in this codebase:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {results.known_divergence_causes.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
